import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import DescriptionIcon from '@mui/icons-material/Description';
import HistoryIcon from '@mui/icons-material/History';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import { SectionContent, GoBackButton, StyledList } from '../../components';
import ChartHistoryDrawer from '../../components/common/ChartHistoryDrawer';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';
import { openPDF } from '../../utils';
import useStrategyAssets from '../../hooks/useStrategyAssets';
import useStrategyPerformance from '../../hooks/useStrategyPerformance';
import useStrategyBenchmarks from '../../hooks/useStrategyBenchmarks';
import ROUTES from '../../config/routes';
import THEME from '../../constants/theme';
import { SECTION_SPACING, ELEMENT_SPACING } from '../../constants';
import dravonLogo from '../../assets/icons/dravon-logo.svg';

const BENCHMARK_SERIES = [
  {
    key: 'dravon',
    label: 'DRAVON*',
    color: THEME.COLORS.PRIMARY_BLUE,
    strokeWidth: 3
  },
  { key: 'lmbaGold', label: 'LMBA/Gold', color: '#8a6d3b', strokeWidth: 2.2 },
  { key: 'nasdaq', label: 'Nasdaq', color: '#3f7fb5', strokeWidth: 2.2 },
  { key: 'sp500', label: 'SPX', color: '#5b8fda', strokeWidth: 2.2 },
  { key: 'dow30', label: 'DOW', color: '#4a6fa5', strokeWidth: 2.2 },
  { key: 'nikkei225', label: 'Nikkei', color: '#9b3f3f', strokeWidth: 2.2 },
  { key: 'ftse', label: 'FTSE', color: '#557a46', strokeWidth: 2.2 },
  { key: 'dax', label: 'DAX', color: '#2f9e8f', strokeWidth: 2.2 }
];

const DravonPage = () => {
  const { latestCharts, chartHistory, documents } = useStrategyAssets('dravon');
  const { performance: strategyPerformance } = useStrategyPerformance('dravon');
  const {
    loading: benchmarksLoading,
    error: benchmarksError,
    benchmarks
  } = useStrategyBenchmarks('dravon');
  const [chartView, setChartView] = useState('week');
  const [chartType, setChartType] = useState('performance');
  const [benchmarkMode, setBenchmarkMode] = useState('growth');
  const [isLoading, setIsLoading] = useState(false);
  const [displayedSrc, setDisplayedSrc] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyChart, setHistoryChart] = useState(null);

  // Set initial chart once CMS assets load (ref prevents stale-closure lint warning)
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasInitialized.current && latestCharts?.week?.performance) {
      hasInitialized.current = true;
      setDisplayedSrc(latestCharts.week.performance);
    }
  }, [latestCharts]);

  const getChartSrc = (view, type) => {
    const period = view === 'week' ? 'week' : 'month';
    return latestCharts?.[period]?.[type] ?? null;
  };

  const updateChart = (nextView, nextType) => {
    const viewToUse = nextView || chartView;
    const typeToUse = nextType || chartType;
    const nextSrc = getChartSrc(viewToUse, typeToUse);

    if (!nextSrc || nextSrc === displayedSrc) return;

    setIsLoading(true);
    const img = new Image();
    img.onload = () => {
      setDisplayedSrc(nextSrc);
      setIsLoading(false);
    };
    img.onerror = () => setIsLoading(false);
    img.src = nextSrc;
  };

  const handleChartViewChange = (e, v) => {
    if (!v || v === chartView) return;
    setChartView(v);
    updateChart(v, chartType);
  };

  const handleChartTypeChange = (e, v) => {
    if (!v || v === chartType) return;
    setChartType(v);
    updateChart(chartView, v);
  };

  const handleDocumentClick = docType => {
    switch (docType) {
      case 'full-factsheet':
        if (documents.factsheet) openPDF(documents.factsheet);
        break;
      case 'strategy-factsheet':
        if (documents.description) openPDF(documents.description);
        break;
      default:
        break;
    }
  };

  const fallbackPerformanceStats = [
    { label: 'Average ROR', value: '8.28%' },
    { label: 'Sharpe Ratio (1Y- Bill)', value: '9.14' },
    { label: 'Total Return Cumulative', value: '48.58%' },
    { label: 'Last Month', value: '4.37%' },
    { label: '3 Month ROR', value: '21.19%' },
    { label: 'Excess Return', value: '38.88%' }
  ];

  const performanceStats =
    strategyPerformance?.performanceStats?.length > 0
      ? strategyPerformance.performanceStats
      : fallbackPerformanceStats;

  const heroTagline =
    strategyPerformance?.hero?.tagline ||
    'Capturing Volatility, Preserving Capital';

  const monthlyBenchmarkRows = React.useMemo(() => {
    const rows = Array.isArray(benchmarks?.months) ? benchmarks.months : [];
    return [...rows].sort((a, b) =>
      String(a.monthKey || '').localeCompare(String(b.monthKey || ''))
    );
  }, [benchmarks?.months]);

  const benchmarkGrowthData = React.useMemo(() => {
    if (!monthlyBenchmarkRows.length) return [];

    const base = BENCHMARK_SERIES.reduce((acc, series) => {
      acc[series.key] = 100;
      return acc;
    }, {});

    return monthlyBenchmarkRows.map(row => {
      const point = {
        label: row.label,
        monthKey: row.monthKey
      };

      BENCHMARK_SERIES.forEach(series => {
        const monthlyReturn = Number(row?.values?.[series.key]?.returnPct || 0);
        base[series.key] *= 1 + monthlyReturn / 100;
        point[series.key] = Number(base[series.key].toFixed(2));
      });

      return point;
    });
  }, [monthlyBenchmarkRows]);

  const benchmarkReturnData = React.useMemo(() => {
    return monthlyBenchmarkRows.map(row => {
      const point = {
        label: row.label,
        monthKey: row.monthKey
      };

      BENCHMARK_SERIES.forEach(series => {
        point[series.key] = Number(row?.values?.[series.key]?.returnPct || 0);
      });

      return point;
    });
  }, [monthlyBenchmarkRows]);

  const benchmarkChartData =
    benchmarkMode === 'growth' ? benchmarkGrowthData : benchmarkReturnData;

  const benchmarkSummary = React.useMemo(() => {
    if (!benchmarkGrowthData.length) return { dravonValue: null };

    const lastPoint = benchmarkGrowthData[benchmarkGrowthData.length - 1];
    return {
      dravonValue: Number(lastPoint?.dravon || 0)
    };
  }, [benchmarkGrowthData]);

  return (
    <>
      <SEO
        title={seoData.dravon.title}
        content={seoData.dravon.content}
        keywords={seoData.dravon.keywords}
        canonical={seoData.dravon.canonical}
        ogImage={seoData.dravon.ogImage}
        ogType={seoData.dravon.ogType}
        structuredData={seoData.dravon.structuredData}
      />
      <Box sx={{ width: '100%', backgroundColor: THEME.COLORS.NEUTRAL_WHITE }}>
        <GoBackButton backTo={ROUTES.STRATEGIES.ROOT} backText='Strategies' />

        {/* Hero Section with Logo */}
        <Box id='hero' sx={{ mb: SECTION_SPACING }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: THEME.SPACING.SMALL
            }}
          >
            <img
              src={dravonLogo}
              alt='Dravon Logo'
              style={{
                height: '110px',
                objectFit: 'contain'
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
              color: THEME.COLORS.NEUTRAL_GRAY_DARK,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM
            }}
          >
            {heroTagline}
          </Typography>
        </Box>

        {/* Performance Overview */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            Performance Overview
          </Typography>

          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: "The chart below highlights DRAVON's consistent return profile, adaptive risk control, and ability to navigate volatile regimes through disciplined execution.",
                fontSize: '1.3rem',
                marginBottom: ELEMENT_SPACING,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              }
            ]}
          />

          <Box
            sx={{
              mb: ELEMENT_SPACING,
              backgroundColor: '#f8f9fb',
              borderRadius: '8px',
              padding: { xs: 2, sm: 3, md: 4 },
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
          >
            {/* ── Chart toggles – hidden while viewing a history chart ── */}
            {!historyChart && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  mb: 1.5
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                    justifyContent: 'flex-start',
                    flex: 1,
                    minWidth: 240
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.9rem',
                      color: THEME.COLORS.NEUTRAL_GRAY_DARK,
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM
                    }}
                  >
                    Chart Type
                  </Typography>
                  <ToggleButtonGroup
                    value={chartType}
                    exclusive
                    onChange={handleChartTypeChange}
                    size='small'
                    aria-label='Dravon chart type'
                    sx={{
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                        px: 1.6,
                        py: 0.55,
                        borderColor: 'transparent',
                        backgroundColor: '#e8f0ff',
                        color: THEME.COLORS.PRIMARY_BLUE,
                        borderRadius: '4px',
                        marginLeft: 0.35,
                        '&:hover': {
                          backgroundColor: THEME.COLORS.PRIMARY_BLUE + '24',
                          borderColor: THEME.COLORS.PRIMARY_BLUE
                        }
                      },
                      '& .MuiToggleButton-root.Mui-selected': {
                        background: `linear-gradient(135deg, ${THEME.COLORS.PRIMARY_BLUE}, #1b84ff)`,
                        color: THEME.COLORS.NEUTRAL_WHITE,
                        borderColor: THEME.COLORS.PRIMARY_BLUE,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        '&:hover': {
                          background: THEME.COLORS.PRIMARY_BLUE,
                          color: THEME.COLORS.NEUTRAL_WHITE
                        }
                      }
                    }}
                  >
                    <ToggleButton
                      value='performance'
                      aria-label='Performance chart'
                    >
                      Performance
                    </ToggleButton>
                    <ToggleButton value='exposure' aria-label='Exposure chart'>
                      Exposure
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <ToggleButtonGroup
                  value={chartView}
                  exclusive
                  onChange={handleChartViewChange}
                  size='small'
                  aria-label='Dravon chart timeframe'
                  sx={{
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      px: 1.6,
                      py: 0.55,
                      borderColor: 'transparent',
                      backgroundColor: '#e8f0ff',
                      color: THEME.COLORS.PRIMARY_BLUE,
                      borderRadius: '4px',
                      marginLeft: 0.35,
                      '&:hover': {
                        backgroundColor: THEME.COLORS.PRIMARY_BLUE + '24',
                        borderColor: THEME.COLORS.PRIMARY_BLUE
                      }
                    },
                    '& .MuiToggleButton-root[aria-label="Monthly view"]': {
                      backgroundColor: THEME.COLORS.NEUTRAL_GRAY_LIGHT + '55',
                      color: THEME.COLORS.NEUTRAL_GRAY,
                      '&:hover': {
                        backgroundColor: THEME.COLORS.NEUTRAL_GRAY_LIGHT + '55'
                      }
                    },
                    '& .MuiToggleButton-root.Mui-disabled': {
                      opacity: 1,
                      color: THEME.COLORS.NEUTRAL_GRAY,
                      backgroundColor: THEME.COLORS.NEUTRAL_GRAY_LIGHT + '55'
                    },
                    '& .MuiToggleButton-root.Mui-selected': {
                      background: `linear-gradient(135deg, ${THEME.COLORS.PRIMARY_BLUE}, #1b84ff)`,
                      color: THEME.COLORS.NEUTRAL_WHITE,
                      borderColor: THEME.COLORS.PRIMARY_BLUE,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      '&:hover': {
                        background: THEME.COLORS.PRIMARY_BLUE,
                        color: THEME.COLORS.NEUTRAL_WHITE
                      }
                    }
                  }}
                >
                  <ToggleButton value='week' aria-label='Weekly view'>
                    Weekly
                  </ToggleButton>
                  <ToggleButton value='month' aria-label='Monthly view'>
                    Monthly
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {/* ── History toolbar – below chart type buttons ──────────────────── */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: historyChart ? 'space-between' : 'flex-start',
                mb: historyChart ? 1.5 : 1,
                mt: !historyChart ? 1.5 : 0
              }}
            >
              <Button
                size='small'
                variant={historyOpen ? 'contained' : 'outlined'}
                startIcon={<HistoryIcon />}
                onClick={() => setHistoryOpen(h => !h)}
                aria-expanded={historyOpen}
                aria-controls='dravon-history'
                sx={{
                  textTransform: 'none',
                  px: 1.4,
                  py: 0.5,
                  borderColor: THEME.COLORS.PRIMARY_BLUE,
                  color: historyOpen
                    ? THEME.COLORS.NEUTRAL_WHITE
                    : THEME.COLORS.PRIMARY_BLUE,
                  backgroundColor: historyOpen
                    ? THEME.COLORS.PRIMARY_BLUE
                    : 'transparent',
                  fontSize: '0.82rem',
                  fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                  '&:hover': {
                    backgroundColor: historyOpen
                      ? THEME.COLORS.PRIMARY_BLUE
                      : THEME.COLORS.PRIMARY_BLUE + '14',
                    borderColor: THEME.COLORS.PRIMARY_BLUE
                  }
                }}
              >
                History
              </Button>
              {historyChart && (
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    color: THEME.COLORS.NEUTRAL_GRAY,
                    fontStyle: 'italic'
                  }}
                >
                  Viewing:&nbsp;
                  <Box
                    component='span'
                    sx={{
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                      fontStyle: 'normal',
                      color: THEME.COLORS.NEUTRAL_GRAY_DARK
                    }}
                  >
                    {historyChart}
                  </Box>
                </Typography>
              )}
            </Box>

            <ChartHistoryDrawer
              id='dravon-history'
              open={historyOpen}
              onClose={() => setHistoryOpen(false)}
              chartHistory={chartHistory}
              onSelectChart={(url, label) => {
                setDisplayedSrc(url);
                setHistoryChart(label);
                setHistoryOpen(true);
              }}
            />
            <Box
              sx={{
                position: 'relative',
                minHeight: { xs: 200, sm: 260, md: 320 }
              }}
              aria-busy={isLoading || !displayedSrc}
            >
              <Box
                component='img'
                src={displayedSrc || ''}
                alt={`Dravon Chart - ${
                  chartType === 'performance' ? 'Performance' : 'Exposure'
                } ${chartView === 'week' ? 'Weekly' : 'Monthly'}`}
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  borderRadius: '4px',
                  opacity: isLoading || !displayedSrc ? 0 : 1,
                  transition: 'opacity 0.2s ease'
                }}
                onLoad={() => setIsLoading(false)}
                onError={() => setIsLoading(false)}
              />
              {(isLoading || !displayedSrc) && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CircularProgress
                    sx={{ color: THEME.COLORS.PRIMARY_BLUE }}
                    size={32}
                  />
                </Box>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              mt: { xs: 3, md: 4 },
              border: `1px solid ${THEME.COLORS.NEUTRAL_GRAY_LIGHT}`,
              borderRadius: '8px',
              padding: { xs: 2, sm: 3, md: 3.5 },
              backgroundColor: THEME.COLORS.NEUTRAL_WHITE,
              boxShadow: THEME.SHADOWS.SMALL
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
                color: THEME.COLORS.PRIMARY_BLUE,
                mb: 1
              }}
            >
              DRAVON vs major market benchmarks
            </Typography>
            <Typography
              sx={{
                color: THEME.COLORS.NEUTRAL_GRAY,
                fontSize: { xs: '0.95rem', md: '1rem' },
                lineHeight: 1.7,
                mb: 2
              }}
            >
              Compare DRAVON against LMBA/Gold, Nasdaq, SP500, DOW30, Nikkei225,
              FTSE and DAX using monthly benchmark tracking.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {benchmarkSummary.dravonValue && (
                <Chip
                  label={`DRAVON growth*: ${benchmarkSummary.dravonValue.toFixed(2)}`}
                  sx={{
                    color: THEME.COLORS.PRIMARY_BLUE,
                    border: `1px solid ${THEME.COLORS.PRIMARY_BLUE}55`,
                    backgroundColor: `${THEME.COLORS.PRIMARY_BLUE}14`,
                    fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD
                  }}
                />
              )}
              <Chip
                label='*Net & After fees'
                sx={{
                  color: THEME.COLORS.NEUTRAL_GRAY_DARK,
                  border: `1px solid ${THEME.COLORS.NEUTRAL_GRAY_LIGHT}`,
                  backgroundColor: `${THEME.COLORS.NEUTRAL_GRAY_LIGHT}55`
                }}
              />
            </Box>

            <ToggleButtonGroup
              value={benchmarkMode}
              exclusive
              onChange={(_, nextMode) => {
                if (nextMode) setBenchmarkMode(nextMode);
              }}
              aria-label='Dravon benchmark chart mode'
              sx={{
                mb: 2.5,
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  borderColor: `${THEME.COLORS.PRIMARY_BLUE}55`,
                  color: THEME.COLORS.PRIMARY_BLUE,
                  fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                  '&.Mui-selected': {
                    color: THEME.COLORS.NEUTRAL_WHITE,
                    backgroundColor: THEME.COLORS.PRIMARY_BLUE,
                    borderColor: THEME.COLORS.PRIMARY_BLUE
                  }
                }
              }}
            >
              <ToggleButton value='growth' aria-label='Benchmark NAV mode'>
                NAV
              </ToggleButton>
              <ToggleButton
                value='returns'
                aria-label='Benchmark monthly returns mode'
              >
                Monthly Returns
              </ToggleButton>
            </ToggleButtonGroup>

            {benchmarksLoading && benchmarkChartData.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress
                  sx={{ color: THEME.COLORS.PRIMARY_BLUE }}
                  size={32}
                />
              </Box>
            ) : benchmarkChartData.length === 0 ? (
              <Box
                sx={{
                  border: `1px solid ${THEME.COLORS.NEUTRAL_GRAY_LIGHT}`,
                  borderRadius: '6px',
                  p: 2,
                  backgroundColor: '#f8f9fb'
                }}
              >
                <Typography
                  sx={{ color: THEME.COLORS.NEUTRAL_GRAY, fontSize: '0.9rem' }}
                >
                  {benchmarksError
                    ? 'Benchmark comparison data is temporarily unavailable. Please try again later.'
                    : 'Benchmark comparison data is not available yet.'}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{ height: { xs: 280, md: 360 } }}
                role='img'
                aria-label='Dravon monthly benchmark comparison chart for DRAVON, LMBA Gold, Nasdaq, SP500, DOW30, Nikkei225, FTSE and DAX'
              >
                <ResponsiveContainer width='100%' height='100%'>
                  <LineChart
                    data={benchmarkChartData}
                    margin={{ top: 8, right: 12, left: -8, bottom: 4 }}
                  >
                    <CartesianGrid stroke='#d8deea' strokeDasharray='3 3' />
                    <XAxis
                      dataKey='label'
                      stroke={THEME.COLORS.NEUTRAL_GRAY}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      stroke={THEME.COLORS.NEUTRAL_GRAY}
                      tick={{ fontSize: 12 }}
                      tickFormatter={value =>
                        benchmarkMode === 'growth'
                          ? Number(value).toFixed(0)
                          : `${Number(value).toFixed(0)}%`
                      }
                    />
                    <Tooltip
                      contentStyle={{
                        border: `1px solid ${THEME.COLORS.NEUTRAL_GRAY_LIGHT}`,
                        borderRadius: '8px',
                        backgroundColor: THEME.COLORS.NEUTRAL_WHITE
                      }}
                      formatter={(value, name) => {
                        const valueLabel =
                          benchmarkMode === 'growth'
                            ? Number(value).toFixed(2)
                            : `${Number(value).toFixed(2)}%`;
                        const seriesLabel =
                          name === 'DRAVON*' ? 'DRAVON*' : name;
                        return [valueLabel, seriesLabel];
                      }}
                    />
                    <Legend
                      wrapperStyle={{ color: THEME.COLORS.NEUTRAL_GRAY_DARK }}
                    />
                    {BENCHMARK_SERIES.map(series => (
                      <Line
                        key={series.key}
                        type='monotone'
                        dataKey={series.key}
                        name={series.label}
                        stroke={series.color}
                        strokeWidth={series.strokeWidth}
                        dot={{ r: 2.6 }}
                        activeDot={{ r: 4.2 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>
        </Box>

        {/* Performance Statistics */}
        <Box
          sx={{
            backgroundColor: THEME.COLORS.NEUTRAL_WHITE,
            border: `1px solid ${THEME.COLORS.NEUTRAL_GRAY_LIGHT}`,
            borderRadius: '8px',
            padding: { xs: 3, sm: 4 },
            mb: SECTION_SPACING
          }}
        >
          <Typography
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontSize: '1.5rem',
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: 3,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            Performance Statistics
          </Typography>
          <Grid container spacing={3}>
            {performanceStats.map(stat => (
              <Grid item xs={12} sm={6} md={4} key={stat.label}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '1rem',
                      color: THEME.COLORS.NEUTRAL_GRAY_DARK,
                      mb: 0.5
                    }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '2.5rem',
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
                      color: THEME.COLORS.NEUTRAL_BLACK
                    }}
                  >
                    {stat.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Introduction Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            Introduction
          </Typography>

          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: "DRAVON is a volatility-focused systematic strategy within MarQ Capital's quantitative research division.",
                fontSize: '1.3rem',
                marginBottom: ELEMENT_SPACING,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              },
              {
                text: 'Its objective is to identify and capture asymmetric opportunities in global volatility markets, transforming uncertainty into a source of consistent, risk-adjusted alpha.',
                fontSize: '1.3rem',
                marginBottom: 0,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              }
            ]}
          />
        </Box>

        {/* Key Features Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            Key Features
          </Typography>

          <Box sx={{ mb: SECTION_SPACING }}>
            <StyledList
              variant='numbered'
              items={[
                {
                  title: 'Volatility Signal Architecture',
                  text: 'DRAVON employs models that analyze implied vs. realized volatility, cross-asset spreads, and skew behavior, seeking to identify moments of volatility mispricing and expansion.'
                },
                {
                  title: 'Market-Neutral Structure',
                  text: 'Designed to remain neutral to directional market bias, DRAVON aims to profit from volatility shifts, dislocations, and tail-risk events across cycles.'
                },
                {
                  title: 'Systematic Risk-First Framework',
                  text: "The strategy's incubation process integrates multi-layered risk controls, dynamic position sizing, and live simulation environments to validate robustness before full deployment."
                },
                {
                  title: 'Institutional Alignment',
                  text: "Built under the same operational discipline as MarQ Capital's live programs, DRAVON will be offered through regulated, segregated structures upon launch."
                }
              ]}
            />
          </Box>
        </Box>

        {/* Why DRAVON Section */}
        <Box
          sx={{
            mb: SECTION_SPACING,
            backgroundColor: THEME.COLORS.PRIMARY_BLUE,
            borderRadius: '8px',
            padding: { xs: 3, sm: 4, md: 5 },
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.NEUTRAL_WHITE,
              mb: ELEMENT_SPACING
            }}
          >
            Why DRAVON
          </Typography>

          <Typography
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: '1.3rem',
              color: THEME.COLORS.NEUTRAL_WHITE,
              fontStyle: 'italic',
              lineHeight: 1.8,
              mb: ELEMENT_SPACING
            }}
          >
            While many strategies seek to avoid volatility, DRAVON analyzes it,
            transforming market turbulence into a structured, repeatable source
            of advantage.
          </Typography>

          <Typography
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: '1.3rem',
              color: THEME.COLORS.NEUTRAL_WHITE,
              fontStyle: 'italic',
              lineHeight: 1.8,
              mb: ELEMENT_SPACING
            }}
          >
            Following an extensive incubation period, during which the model
            matured through empirical validation, stress-scenario evaluation,
            and operational refinement, DRAVON is now fully deployed and
            accessible to institutional allocators.
          </Typography>

          <Typography
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: '1.3rem',
              color: THEME.COLORS.NEUTRAL_WHITE,
              fontStyle: 'italic',
              lineHeight: 1.8
            }}
          >
            This live deployment reflects the strategy’s readiness: its
            execution framework, risk controls and cross-asset adaptability have
            met MarQ Capital’s standards for systematic robustness and
            operational precision.
          </Typography>
        </Box>

        {/* Related Documents Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            Related Documents
          </Typography>
          <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                variant='outlined'
                startIcon={<DescriptionIcon />}
                onClick={() => handleDocumentClick('full-factsheet')}
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: '80px',
                  padding: 3,
                  borderColor: THEME.COLORS.PRIMARY_BLUE,
                  color: THEME.COLORS.PRIMARY_BLUE,
                  fontSize: '1.1rem',
                  fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                  textTransform: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  '&:hover': {
                    borderColor: THEME.COLORS.PRIMARY_BLUE,
                    backgroundColor: THEME.COLORS.PRIMARY_BLUE_LIGHT + '22'
                  }
                }}
              >
                <Box>Full Factsheet</Box>
                <Box sx={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
                  Complete strategy overview
                </Box>
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                variant='outlined'
                startIcon={<DescriptionIcon />}
                onClick={() => handleDocumentClick('strategy-factsheet')}
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: '80px',
                  padding: 3,
                  borderColor: THEME.COLORS.PRIMARY_BLUE,
                  color: THEME.COLORS.PRIMARY_BLUE,
                  fontSize: '1.1rem',
                  fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                  textTransform: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  '&:hover': {
                    borderColor: THEME.COLORS.PRIMARY_BLUE,
                    backgroundColor: THEME.COLORS.PRIMARY_BLUE_LIGHT + '22'
                  }
                }}
              >
                <Box>Strategy Description</Box>
                <Box sx={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
                  Key metrics & methodology
                </Box>
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Contact Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            Get in Touch
          </Typography>

          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: 'For more information about DRAVON or to discuss how this strategy may complement your portfolio, please contact our team.',
                fontSize: '1.3rem',
                marginBottom: ELEMENT_SPACING,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              },
              {
                text: 'This information has been prepared solely for informational purposes and is not an offer to buy or sell or a solicitation of an offer to buy or sell any security or instrument or to participate in any trading strategy. Past performance is not necessarily indicative of future results.',
                fontSize: '1rem',
                marginBottom: 0,
                color: THEME.COLORS.NEUTRAL_GRAY,
                fontStyle: 'italic'
              }
            ]}
          />
        </Box>
      </Box>
    </>
  );
};

export default DravonPage;
